import { useState } from 'react'
import { cn } from '@/lib/utils'

interface ToggleableAmountProps {
  /** Formatted token amount string (already formatted with proper decimals) */
  amount: string
  /** Token symbol (e.g., "WELEPHANT", "BNB", "stWELEPHANT") */
  symbol: string
  /** USD value as a formatted string (e.g., "45.67") or null if unavailable */
  usdValue?: string | null
  /** Hide the suffix label (use when an icon already identifies the token) */
  hideLabel?: boolean
  /** Show "USD" suffix when toggled to dollar values (default: false, since $ is self-documenting) */
  showUSD?: boolean
  /** Additional classes for the container */
  className?: string
  /** Classes for the amount text */
  amountClassName?: string
  /** Classes for the symbol/label text */
  labelClassName?: string
}

/**
 * A toggleable amount display component.
 * Shows token amount by default, toggles to USD on click.
 * The amount has a dashed underline to indicate it's interactive.
 */
export function ToggleableAmount({
  amount,
  symbol,
  usdValue,
  hideLabel,
  showUSD = false,
  className,
  amountClassName,
  labelClassName,
}: ToggleableAmountProps) {
  const [showUsd, setShowUsd] = useState(false)

  const hasUsdValue = usdValue !== null && usdValue !== undefined && usdValue !== '0.00'

  const handleToggle = () => {
    if (hasUsdValue) {
      setShowUsd(!showUsd)
    }
  }

  return (
    <span
      className={cn(
        'inline-flex items-baseline gap-1',
        hasUsdValue && 'cursor-pointer',
        className
      )}
      onClick={handleToggle}
      title={hasUsdValue ? (showUsd ? `${amount} ${symbol}` : `$${usdValue}`) : undefined}
    >
      <span
        className={cn(
          'transition-all duration-200',
          hasUsdValue && 'border-b border-dashed border-current',
          amountClassName
        )}
      >
        {showUsd ? `$${usdValue}` : amount}
      </span>
      {!hideLabel && (!showUsd || showUSD) && (
        <span className={cn('text-muted-foreground', labelClassName)}>
          {showUsd ? 'USD' : symbol}
        </span>
      )}
    </span>
  )
}

export default ToggleableAmount
